import { Sine, TimelineLite } from "gsap";
import * as HLF from "html-living-framework";
import { Application } from "../Application";
import { AppProgress } from "../Data/AppProgress";
import * as Constants from "../Data/Constants";
import { GalleryHieroglyphData } from "../Data/GalleryHieroglyphData";
import { GameType } from "../Data/GameData";
import * as VoiceOver from "../Data/VoiceOver";
import { GalleryHieroglyphRoll } from "../GameObjects/GalleryHieroglyphRoll";
import { GalleryItem } from "../GameObjects/GalleryItem";
import { HieroglyphCanvas } from "../GameObjects/HieroglyphCanvas";

export class GalleryHieroglyph extends GalleryItem {
    private _canvasContainer!: PIXI.Container;
    private _canvas!: HieroglyphCanvas;
    private _canvasMask!: PIXI.Graphics;
    private _rollLeft!: GalleryHieroglyphRoll;
    private _rollRight!: GalleryHieroglyphRoll;

    constructor(pCallback: (pItem: GalleryItem) => void) {
        super(GameType.HIEROGLYPH, pCallback);

        this._homePos = new PIXI.Point(320, 38);
        this._homeScale = 0.4;
        this._presentScale = 1.0;
        this._targetState = Constants.STATE_HIEROGLYPH;
        this._hoverVO = VoiceOver.HIERO_BUTTON;

        this.createVisuals();

        this._visuals.position.copy(this._homePos);
    }

    protected get saveData(): GalleryHieroglyphData {
        return AppProgress.games[this._gameType].saveData[0] as GalleryHieroglyphData;
    }

    public presentItem(): TimelineLite {
        this.interactive = false;
        this._canvasContainer!.scale.set(this._presentScale);
        this._visuals.position.set(-this._size.x * 0.5 - this._visuals.width * 0.5, -100);

        const timeline: TimelineLite = new TimelineLite();
        timeline.to(this._visuals!, 1.0, {
            x: 0,
            y: -100,
            ease: Sine.easeInOut,
        });
        return timeline;
    }

    public animateItem(): TimelineLite {
        const timeline: TimelineLite = new TimelineLite({
            onUpdate: this.onAnimateItemUpdate.bind(this),
        });
        timeline.add(this._rollLeft.playOpenAnimation(), 0.25);
        timeline.add(this._rollRight.playOpenAnimation(), 0.25);
        return timeline;
    }

    public moveItemToWall(): TimelineLite {
        const moveDur: number = 1.0;

        const timeline: TimelineLite = new TimelineLite();
        // x pos
        timeline.to(this._visuals, moveDur * 0.4, {
            x: this._homePos.x + 425,
            ease: Sine.easeInOut,
        }, 0);
        timeline.to(this._visuals, moveDur * 0.6, {
            x: this._homePos.x,
            ease: Sine.easeInOut,
        }, moveDur * 0.4);
        // y pos
        timeline.to(this._visuals, moveDur * 0.4, {
            y: this._homePos.y - 200,
            ease: Sine.easeInOut,
        }, 0);
        timeline.to(this._visuals, moveDur * 0.6, {
            y: this._homePos.y,
            ease: Sine.easeInOut,
        }, moveDur * 0.4);
        // scale
        timeline.to(this._canvasContainer.scale, moveDur, {
            x: this._homeScale,
            y: this._homeScale,
            ease: Sine.easeInOut,
        }, 0);
        // rotation
        timeline.to(this._visuals, moveDur * 0.35, {
            rotation: 0.03,
            ease: Sine.easeInOut,
        }, 0);
        timeline.to(this._visuals, moveDur * 0.4, {
            rotation: -0.03,
            ease: Sine.easeInOut,
        }, moveDur * 0.35);
        timeline.to(this._visuals, moveDur * 0.25, {
            rotation: 0,
            ease: Sine.easeInOut,
        }, moveDur * 0.75);

        return timeline;
    }

    public cacheVisuals(): PIXI.Texture {
        this._canvasContainer.scale.set(1);
        const texture: PIXI.Texture = Application.instance.renderer.generateTexture(
            this._visuals,
            undefined,
            Application.instance.renderer.resolution,
        );
        this._canvasContainer.scale.set(this._homeScale);
        return texture;
    }

    protected createVisualDefault(): void {
        const image: PIXI.Sprite = HLF.PixiUtils.makeSprite("menu-button-hieroglyph-bw", Constants.SPRITESHEET_MENU);
        this._visuals.addChild(image);
    }

    protected createVisualComplete(): void {
        this._canvasContainer = new PIXI.Container();
        this._visuals.addChild(this._canvasContainer);

        this._canvas = new HieroglyphCanvas(false);
        for (let i = 0; i < this.saveData.contentIndeces.length; ++i) {
            this._canvas.updateLayer(i, this.saveData.contentIndeces[i], true);
        }
        this._canvas.updateColour(this.saveData.colourIndex);
        this._canvas.updateWeather(this.saveData.weatherIndex, true);
        this._canvasContainer.addChild(this._canvas);

        this._canvasMask = new PIXI.Graphics();
        this._canvasMask.beginFill(0x000000);
        this._canvasMask.drawRect(
            -this._canvas.width * 0.5,
            -this._canvas.height * 0.5,
            this._canvas.width,
            this._canvas.width,
        );
        this._canvasMask.endFill();
        this._canvas.addChild(this._canvasMask);
        this._canvas.mask = this._canvasMask;

        this._rollRight = new GalleryHieroglyphRoll();
        this._rollRight.rolledPerc = 0.01;
        this._canvasContainer.addChild(this._rollRight);

        this._rollLeft = new GalleryHieroglyphRoll();
        this._rollLeft.rolledPerc = 0.01;
        this._rollLeft.scale.x = -1;
        this._canvasContainer.addChild(this._rollLeft);

        this.onAnimateItemUpdate();
    }

    protected createVisualCached(): void {
        super.createVisualCached();
        this._cached!.scale.set(this._homeScale);
    }

    private onAnimateItemUpdate(): void {
        this._canvasMask.width = this._canvas.width * this._rollLeft.rolledPerc;
        this._canvas.scale.x =  (0.7 + Math.min(this._rollLeft.rolledPerc * 1.2, 1) * 0.3);
    }
}
